import alert_mixin from '@/utils/mixins/alert_mixin';
import axios from 'axios';
import {
  CREATE_VERIFICATION_FORM,
  GET_VERIFICATION_FORMS,
  GET_LINKED_ONBOARDING_FORMS_API,
  GET_ONBOARDING_FORMS_API,
  LINK_VERIFICATION_FORM_API,
  UNASSIGN_LINKED_ONBOARDING_FORMS_API,
  get_request_config,
} from '@/utils/system_constants/constant.js';

export default {
  name: 'VerificationForm',
  mixins: [alert_mixin],
  data: () => ({
    loading_forms: false,
    verification_forms: [],
    form_name: '',
    form_description: '',
    create_form_running: false,
    create_form_is_active: true,
    selected_assign_verification_form: null,
    onboarding_forms: [],
    loading_onboarding_forms: false,
    loading_linked_onboarding_forms: false,
    linking_onboarding_form_id: null,
    linking_onboarding_form_action: null,
    onboarding_links: {},
    linked_onboarding_forms: [],
  }),
  computed: {
    assign_modal_forms() {
      const onboarding_forms = Array.isArray(this.onboarding_forms) ? this.onboarding_forms : [];
      const linked_onboarding_forms = Array.isArray(this.linked_onboarding_forms) ? this.linked_onboarding_forms : [];

      const onboarding_ids = new Set(onboarding_forms.map((form) => String(form?.id)).filter(Boolean));

      const missing_linked = linked_onboarding_forms
        .map((item) => this.normalize_linked_onboarding_form(item))
        .filter((form) => form?.id != null && form.is_active !== false && !onboarding_ids.has(String(form.id)));

      return [...missing_linked, ...onboarding_forms];
    },
  },
  created() {
    this.fetch_verification_forms();
  },
  methods: {
    normalizeId(value) {
      return String(value == null ? '' : value).trim();
    },

    getLinkedOnboardingFormId(link) {
      if (!link) return '';
      const direct = link.onboarding_form_id;
      if (direct != null) return this.normalizeId(direct);
      const nested = link.onboarding_form?.id;
      return this.normalizeId(nested);
    },

    getResponseItems(parsed) {
      const items = parsed && (parsed.data || parsed.results);
      return Array.isArray(items) ? items : [];
    },

    hasResponseError(parsed) {
      return parsed?.status && parsed.status !== 'success';
    },

    getResponseErrorMessage(parsed) {
      return parsed?.message || 'Failed to load linked onboarding forms.';
    },

    format_created_at(value) {
      if (!value) return '—';

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);

      try {
        return new Intl.DateTimeFormat(undefined, {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }).format(date);
      } catch {
        return date.toLocaleString();
      }
    },

    parse_json_if_string(value) {
      if (typeof value !== 'string') return value;
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    },

    parse_response_data(response) {
      let data = response && response.data != null ? response.data : response != null ? response : {};
      data = this.parse_json_if_string(data);
      return typeof data === 'string' ? {} : data;
    },

    async fetch_verification_forms() {
      this.loading_forms = true;

      try {
        const response = await axios.get(GET_VERIFICATION_FORMS, get_request_config());
        const parsed = this.parse_response_data(response);
        const items = parsed && parsed.data != null ? parsed.data : [];
        this.verification_forms = Array.isArray(items) ? items : [];
      } catch (error) {
        this.verification_forms = [];
        this.show_error(error?.message || 'Failed to load verification forms.');
      } finally {
        this.loading_forms = false;
      }
    },

    open_create_form_modal() {
      this.form_name = '';
      this.form_description = '';
      this.create_form_is_active = true;
    },

    build_create_form_payload() {
      const payload = {
        form_name: String(this.form_name || '').trim(),
        form_description: String(this.form_description || '').trim(),
        is_active: Boolean(this.create_form_is_active),
      };

      return payload;
    },

    reset_create_form_modal() {
      this.close_modal('create_verification_form');
      this.form_name = '';
      this.form_description = '';
      this.create_form_is_active = true;
    },

    async create_form() {
      this.create_form_running = true;
      const loader = await this.show_loader('Creating form...');

      try {
        const payload = this.build_create_form_payload();
        if (!payload.form_name) {
          this.show_error('Form name is required.');
          return;
        }

        const response = await axios.post(CREATE_VERIFICATION_FORM, payload, get_request_config());
        const parsed_response = this.parse_response_data(response);

        if (parsed_response?.status !== 'success') {
          this.show_error(parsed_response?.message || 'Failed to create form.');
          return;
        }

        this.show_success(parsed_response.message || 'Form created successfully.');
        this.reset_create_form_modal();
        await this.fetch_verification_forms();
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'Failed to create form.';
        this.show_error(message);
      } finally {
        await this.close_loader(loader);
        this.create_form_running = false;
      }
    },

    get_verification_form_id(form) {
      return form ? this.first_non_null(form.verification_form_id, form.form_id, form.id) : null;
    },

    first_non_null(...values) {
      for (const value of values) {
        if (value !== undefined && value !== null) return value;
      }
      return null;
    },

    normalize_linked_onboarding_form(item) {
      const result = {
        id: this.first_non_null(
          item?.onboarding_form_id,
          item?.onboarding_form?.id,
          item?.id
        ),
        form_name: this.first_non_null(
          item?.onboarding_form_name,
          item?.onboarding_form?.form_name
        ),
        form_description: this.first_non_null(
          item?.onboarding_form_description,
          item?.onboarding_form?.form_description
        ),
        is_active: item?.is_active !== false,
        __linked_only: true,
      };

      return result;
    },

    request_linked_onboarding_forms(verification_form_id) {
      return axios
        .get(
          GET_LINKED_ONBOARDING_FORMS_API,
          get_request_config({
            params: { verification_form_id },
          }),
        )
        .then((response) => {
          return this.extract_items_from_response(response);
        })
        .catch((error) => {
          this.show_error(
            error?.response?.data?.message ||
              error?.message ||
              'Failed to load linked onboarding forms.',
          );
          return [];
        });
    },

    extract_items_from_response(response) {
      const parsed = this.parse_response_data(response);

      if (this.hasResponseError(parsed)) {
        this.show_error(this.getResponseErrorMessage(parsed));
      }

      return this.getResponseItems(parsed);
    },

    get_onboarding_form_id(link) {
      if (!link) return null;
      const onboarding_form = link.onboarding_form;
      return this.first_non_null(
        link.onboarding_form_id,
        onboarding_form?.id,
        onboarding_form
      );
    },

    get_form_link_id(link) {
      if (!link) return null;
      return this.first_non_null(link.form_link_id, link.link_id, link.id);
    },

    async open_assign_modal(form) {
      this.selected_assign_verification_form = form || null;
      this.onboarding_links = {};
      this.linked_onboarding_forms = [];

      const verification_form_id = this.get_verification_form_id(this.selected_assign_verification_form);

      if (!verification_form_id) {
        this.show_error('Verification form id not found for linking.');
        return;
      }

      if (!this.onboarding_forms.length) await this.fetch_onboarding_forms();
      await this.fetch_linked_onboarding_forms(verification_form_id);
    },

    async fetch_onboarding_forms() {
      this.loading_onboarding_forms = true;
      try {
        const response = await axios.get(GET_ONBOARDING_FORMS_API, get_request_config());
        const parsed = this.parse_response_data(response);
        const items = parsed ? this.first_non_null(parsed.data, parsed.results, []) : [];
        this.onboarding_forms = Array.isArray(items) ? items : [];
      } catch (error) {
        this.onboarding_forms = [];
        this.show_error(error?.message || 'Failed to load onboarding forms.');
      } finally {
        this.loading_onboarding_forms = false;
      }
    },

    async fetch_linked_onboarding_forms(verification_form_id) {
      this.loading_linked_onboarding_forms = true;
      try {
        const links = await this.request_linked_onboarding_forms(verification_form_id);
        this.linked_onboarding_forms = links;

        const next = {};
        links
          .filter((link) => link?.is_active !== false)
          .map((link) => this.get_onboarding_form_id(link))
          .filter((onboarding_id) => onboarding_id != null)
          .forEach((onboarding_id) => {
            next[String(onboarding_id)] = true;
          });
        this.onboarding_links = next;
      } catch (error) {
        this.onboarding_links = {};
        this.linked_onboarding_forms = [];
        this.show_error(error?.message || 'Failed to load linked onboarding forms.');
      } finally {
        this.loading_linked_onboarding_forms = false;
      }
    },

    async on_toggle_link(onboarding_form, checked) {
      const onboarding_form_id = onboarding_form?.id;
      const verification_form_id = this.get_verification_form_id(this.selected_assign_verification_form);

      if (!onboarding_form_id || !verification_form_id) {
        this.show_error('Missing onboarding or verification form id.');
        return;
      }

      if (!checked) {
        await this.unlink_onboarding_form(onboarding_form_id, verification_form_id);
        return;
      }

      await this.link_onboarding_form(onboarding_form_id, verification_form_id);
    },

    create_updated_links(onboarding_form_id, value) {
      const next = { ...(this.onboarding_links || {}) };
      next[String(onboarding_form_id)] = value;
      return next;
    },

    set_onboarding_link_state(onboarding_form_id, value) {
      this.onboarding_links = this.create_updated_links(onboarding_form_id, value);
    },

    upsert_linked_onboarding_form(link) {
      const onboarding_form_id = this.get_onboarding_form_id(link);
      if (onboarding_form_id == null) return;

      const links = Array.isArray(this.linked_onboarding_forms)
        ? [...this.linked_onboarding_forms]
        : [];
      const existing_index = links.findIndex(
        (existing) => String(this.get_onboarding_form_id(existing)) === String(onboarding_form_id)
      );

      if (existing_index >= 0) links.splice(existing_index, 1, link);
      else links.push(link);

      this.linked_onboarding_forms = links;
    },

    async link_onboarding_form(onboarding_form_id, verification_form_id) {
      this.linking_onboarding_form_id = onboarding_form_id;
      this.linking_onboarding_form_action = 'link';

      try {
        const payload = { onboarding_form_id, verification_form_id };
        const response = await axios.post(LINK_VERIFICATION_FORM_API, payload, get_request_config());
        const parsed = this.parse_response_data(response);

        if (parsed?.status === 'success') {
          this.set_onboarding_link_state(onboarding_form_id, true);
          if (parsed?.data) this.upsert_linked_onboarding_form(parsed.data);
          this.show_success(parsed?.message || 'Verification form linked successfully.');
          return;
        }

        this.set_onboarding_link_state(onboarding_form_id, false);
        this.show_error(parsed?.message || 'Failed to link verification form.');
      } catch (error) {
        this.set_onboarding_link_state(onboarding_form_id, false);
        const message = error?.response?.data?.message || error?.message || 'Failed to link verification form.';
        this.show_error(message);
      } finally {
        this.linking_onboarding_form_id = null;
        this.linking_onboarding_form_action = null;
      }
    },

    mark_linked_onboarding_form_inactive(onboarding_form_id, link = null) {
      const onboardingFormId = this.normalizeId(onboarding_form_id);

      if (!onboardingFormId) return;

      const links = Array.isArray(this.linked_onboarding_forms)
        ? this.linked_onboarding_forms
        : [];

      this.linked_onboarding_forms = links.map((existing) => {
        if (this.getLinkedOnboardingFormId(existing) !== onboardingFormId) return existing;
        return { ...existing, ...(link || {}), is_active: false };
      });
    },

    async unlink_onboarding_form(onboarding_form_id, verification_form_id) {
      this.linking_onboarding_form_id = onboarding_form_id;
      this.linking_onboarding_form_action = 'unlink';

      try {
        const response = await axios.post(
          UNASSIGN_LINKED_ONBOARDING_FORMS_API,
          { onboarding_form_id, verification_form_id },
          get_request_config(),
        );

        const parsed = this.parse_response_data(response);

        if (parsed?.status === 'success') {
          this.set_onboarding_link_state(onboarding_form_id, false);
          this.mark_linked_onboarding_form_inactive(onboarding_form_id, parsed?.data);
          const success_msg = parsed?.message || 'Verification form unlinked successfully.';
          this.show_success(success_msg);
          return;
        }

        this.set_onboarding_link_state(onboarding_form_id, true);
        const error_msg = parsed?.message || 'Failed to unlink verification form.';
        this.show_error(error_msg);
      } catch (error) {
        const error_msg = error?.response?.data?.message || error?.message || 'Failed to unlink verification form.';
        this.set_onboarding_link_state(onboarding_form_id, true);
        this.show_error(error_msg);
      } finally {
        this.linking_onboarding_form_id = null;
        this.linking_onboarding_form_action = null;
      }
    },

    redirect_to_configure_services(form) {
      const verification_form_id = (form && (form.verification_form_id || form.form_id || form.id)) || null;
      const route = { name: 'ConfigureVerificationForm' };
      if (verification_form_id) route.query = { verification_form_id: String(verification_form_id) };
      this.$router.push(route);
    },
  },
};
